import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { HotToastService } from "@ngneat/hot-toast";

interface InternalRouteLockState {
  failCount: number;
  lockUntilEpochMs: number;
}

@Injectable({ providedIn: "root" })
export class InternalAccCreationGuard {
  private readonly sessionKey = "internal-acc-creation-unlocked";
  private readonly lockStateKey = "internal-acc-creation-lock-state";
  private readonly pbkdf2Salt = "clienthub-acc-creation-v1";
  private readonly pbkdf2Iterations = 210000;
  private readonly expectedHashHex =
    "e13291fa9e6e21e59eafb4477fa8ffdb24b8fb724dd2cb90c19116a313f83484";

  constructor(
    private readonly router: Router,
    private readonly toastr: HotToastService,
  ) {}

  async canActivate(): Promise<boolean> {
    if (sessionStorage.getItem(this.sessionKey) === "1") {
      return true;
    }

    const lockState = this.getLockState();
    const now = Date.now();
    if (lockState.lockUntilEpochMs > now) {
      const retryInSeconds = Math.ceil((lockState.lockUntilEpochMs - now) / 1000);
      this.toastr.error(
        `Access temporarily locked. Try again in ${retryInSeconds}s.`,
      );
      this.router.navigate(["/trial"]);
      return false;
    }

    const input = window.prompt("Enter password to access this page:");
    if (input === null) {
      this.router.navigate(["/trial"]);
      return false;
    }

    const isValid = await this.verifyPassword(input);
    if (isValid) {
      sessionStorage.setItem(this.sessionKey, "1");
      this.clearLockState();
      return true;
    }

    const updatedFailCount = lockState.failCount + 1;
    const lockSeconds = this.calculateLockSeconds(updatedFailCount);
    this.setLockState({
      failCount: updatedFailCount,
      lockUntilEpochMs: now + lockSeconds * 1000,
    });

    this.toastr.error(`Wrong password. Try again in ${lockSeconds}s.`);
    this.router.navigate(["/trial"]);
    return false;
  }

  private calculateLockSeconds(failCount: number): number {
    return Math.min(5 * 2 ** (failCount - 1), 3600);
  }

  private getLockState(): InternalRouteLockState {
    try {
      const raw = localStorage.getItem(this.lockStateKey);
      if (!raw) {
        return { failCount: 0, lockUntilEpochMs: 0 };
      }

      const parsed = JSON.parse(raw) as Partial<InternalRouteLockState>;
      return {
        failCount: parsed.failCount ?? 0,
        lockUntilEpochMs: parsed.lockUntilEpochMs ?? 0,
      };
    } catch {
      return { failCount: 0, lockUntilEpochMs: 0 };
    }
  }

  private setLockState(state: InternalRouteLockState): void {
    localStorage.setItem(this.lockStateKey, JSON.stringify(state));
  }

  private clearLockState(): void {
    localStorage.removeItem(this.lockStateKey);
  }

  private async verifyPassword(candidate: string): Promise<boolean> {
    const derivedHash = await this.deriveHashHex(candidate);
    return derivedHash === this.expectedHashHex;
  }

  private async deriveHashHex(value: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      "raw",
      encoder.encode(value),
      "PBKDF2",
      false,
      ["deriveBits"],
    );

    const bits = await crypto.subtle.deriveBits(
      {
        name: "PBKDF2",
        salt: encoder.encode(this.pbkdf2Salt),
        iterations: this.pbkdf2Iterations,
        hash: "SHA-256",
      },
      keyMaterial,
      256,
    );

    const bytes = new Uint8Array(bits);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, "0"))
      .join("");
  }
}
